import type { ToastServiceMethods } from 'primevue/toastservice'
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import routes from './routes'
import { ROUTES } from './routeNames'
import middlewarePipeline from './middleware/middlewarePipeline'
import ensureResourceExists from './middleware/ensureResourceExists'
import { ref } from 'vue'
import { buildContextFromRoute } from '@dashboard/modules/righty/services/rightyContextBuilder'
import { useRecentPages } from '@dashboard/composables/useRecentPages'
import { useRightyStore } from '@dashboard/modules/righty/stores/rightyStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { storeToRefs } from 'pinia'

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to: any, from: any, savedPosition: any) {
    // If only query changed (like tab switching), don't scroll
    if (to.path === from.path && to.hash === from.hash) {
      return false
    }

    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition
    }

    // Otherwise scroll to top
    return { top: 0 }
  }
})
let routerToast: ToastServiceMethods
let emailVerificationResult: '1' | '0' | null = null

export function configureRouterToast(toast: ToastServiceMethods) {
  routerToast = toast
}

// Track the lastMeaningfulSaveTime when we last showed the notification
const lastNotifiedSaveTime = ref(0)

// Define route to analytics event mapping
const ROUTE_ANALYTICS_MAP: Record<string, string> = {
  [ROUTES.SEGMENTGROUPS]: AnalyticsEventTypes.VIEWED_SEGMENT_GROUPS,
  [ROUTES.SEGMENTGROUP]: AnalyticsEventTypes.VIEWED_SEGMENT_GROUP_DETAILS,
  [ROUTES.QUESTIONS]: AnalyticsEventTypes.VIEWED_QUESTIONS,
  [ROUTES.QUESTION]: AnalyticsEventTypes.VIEWED_QUESTION_DETAILS,
  [ROUTES.CAMPAIGNS]: AnalyticsEventTypes.VIEWED_CAMPAIGNS,
  [ROUTES.CAMPAIGN]: AnalyticsEventTypes.VIEWED_CAMPAIGN_DETAILS,
  [ROUTES.OFFERFUNNELS]: AnalyticsEventTypes.VIEWED_FLOWS,
  [ROUTES.OFFERFUNNEL]: AnalyticsEventTypes.VIEWED_FLOW_BUILDER,
  [ROUTES.WIDGETS]: AnalyticsEventTypes.VIEWED_WIDGETS,
  [ROUTES.WIDGET]: AnalyticsEventTypes.VIEWED_WIDGET_DETAILS,
  [ROUTES.OFFERS]: AnalyticsEventTypes.VIEWED_OFFERS,
  [ROUTES.OFFER]: AnalyticsEventTypes.VIEWED_OFFER_DETAILS,
  [ROUTES.FORMS]: AnalyticsEventTypes.VIEWED_FORMS,
  [ROUTES.FORM]: AnalyticsEventTypes.VIEWED_FORM_DETAILS,
  [ROUTES.EMAILS]: AnalyticsEventTypes.VIEWED_EMAILS,
  [ROUTES.EMAIL]: AnalyticsEventTypes.VIEWED_EMAIL_DETAILS,
  [ROUTES.GOALS]: AnalyticsEventTypes.VIEWED_GOALS,
  [ROUTES.GOAL]: AnalyticsEventTypes.VIEWED_GOAL_DETAILS,
  [ROUTES.INTEGRATIONS]: AnalyticsEventTypes.VIEWED_INTEGRATIONS,
  [ROUTES.INTEGRATION]: AnalyticsEventTypes.VIEWED_INTEGRATION_DETAILS,
  [ROUTES.INSIGHTS]: AnalyticsEventTypes.VIEWED_INSIGHTS,
  [ROUTES.SETTINGS_GENERAL]: AnalyticsEventTypes.VIEWED_SETTINGS,
  [ROUTES.ACCOUNT_PROFILE]: AnalyticsEventTypes.VIEWED_PROFILE,
  [ROUTES.TEMPLATES]: AnalyticsEventTypes.VIEWED_TEMPLATES,
  [ROUTES.TEMPLATE]: AnalyticsEventTypes.VIEWED_TEMPLATE_DETAILS
}

// Helper function to track page navigation
function trackPageNavigation(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized | null = null,
  isInitialLoad = false
) {
  // Track our custom analytics events
  const routeName = to.name as string
  if (routeName && ROUTE_ANALYTICS_MAP[routeName]) {
    void recordAnalyticsEvent(
      ROUTE_ANALYTICS_MAP[routeName],
      {
        fromPath: from?.path || null,
        fromRouteName: from?.name || null,
        toPath: to.path,
        toRouteName: to.name,
        navigationTime: Date.now(),
        isInitialLoad
      },
      to
    )
  }
}

router.beforeEach((to, from) => {
  routerToast.removeAllGroups()
  if (to.query.email_verified === '1' || to.query.email_verified === '0') {
    emailVerificationResult = to.query.email_verified
    const query = { ...to.query }
    delete query.email_verified
    return { path: to.path, query, hash: to.hash, replace: true }
  }
  const middleware = to.meta.middleware
  if (!middleware) {
    return
  }

  return middlewarePipeline({ to, from, toast: routerToast }, middleware)
})

router.afterEach((to, from, failure) => {
  const configStore = useConfigStore()
  const teamStore = useTeamStore()
  const { isDirty, lastMeaningfulSaveTime } = storeToRefs(configStore)
  const { isDemo } = storeToRefs(teamStore)

  const notificationStore = useNotificationStore()

  // Only show notification if:
  // 1. There are unpublished changes (isDirty is true)
  // 2. There's been a new meaningful save since we last showed the notification
  if (isDirty.value && lastMeaningfulSaveTime.value > lastNotifiedSaveTime.value && !isDemo.value) {
    // notificationStore.add({
    //   type: 'warn',
    //   message: `It looks like you've made a few changes. Make sure to publish to your website when you're ready for them to go live.`,
    //   life: 6000
    // })
    lastNotifiedSaveTime.value = lastMeaningfulSaveTime.value
  }
  if (!failure) {
    if (emailVerificationResult !== null && to.name !== ROUTES.HOME) {
      routerToast.add({
        severity: emailVerificationResult === '1' ? 'success' : 'error',
        summary: emailVerificationResult === '1' ? 'Email address verified' : 'Email verification failed',
        detail:
          emailVerificationResult === '1'
            ? 'You can now invite people outside your organization.'
            : 'This link is invalid or expired. Please request a new verification email.',
        life: 6000
      })
      emailVerificationResult = null
    }
    trackPageNavigation(to, from)

    // Track recent pages for quick navigation
    const { recordVisit } = useRecentPages()
    recordVisit(to)

    // Update Righty context when navigating within a team
    if (to.params.teamPid) {
      const rightyStore = useRightyStore()
      const context = buildContextFromRoute(to, configStore)
      rightyStore.setInvocationContext(context)
    }
  }
})

// Track initial page load
void router.isReady().then(() => {
  const currentRoute = router.currentRoute.value
  trackPageNavigation(currentRoute, null, true)
})

export default router
